import { useMemo } from 'react';
import { AutocompleteProps } from '../components/Autocomplete';

interface Country {
  alpha2: string;
  name: Record<string, string>;
  label: string;
}

interface Item {
  value: string;
  label: string;
  hint: string;
}

const convertCountriesToItems = (countries: Country[], locale: string): Item[] => {
  const lang = locale.substring(0, 2);
  // allow search by chinese and english
  const hint = lang === 'en' ? 'zh' : 'en';
  return countries.map((country) => ({
    value: country.alpha2,
    label: country.label,
    hint: country.name[hint],
  }));
};

const itemToString = (item: Item | null) => item?.label ?? '';
const itemToKey = (item: Item) => item.value;
const itemToSubLabel = (item: Item) => item.value;

const searchOptions = { keys: ['value', 'label', 'hint'], threshold: 0.3 };

interface Props {
  lang: string;
  id: string;
  label?: string;
  noItemsLabel?: string;
  placeholder?: string;
  countries: Country[];
  selectedCountryAlpha2: string | null;
  isError?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  onChange: (countryAlpha2: string | null) => void;
}

export const makeCountryAutocompleteProps = ({
  lang,
  selectedCountryAlpha2,
  countries,
  onChange,
  ...props
}: Props): AutocompleteProps<Item> => {
  const items = convertCountriesToItems(countries, lang);

  const selectedItem = selectedCountryAlpha2
    ? items.find((item) => item.value === selectedCountryAlpha2) ?? null
    : null;

  const onSelectedItemChange = (selectedItem: Item | null | undefined) => {
    onChange(selectedItem?.value ?? null);
  };

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.currentTarget.select();
  };

  return {
    ...props,
    name: props.id,
    items,
    selectedItem,
    searchOptions,
    itemToKey,
    itemToString,
    itemToSubLabel,
    onSelectedItemChange,
    onFocus,
  };
};

export const useCountryAutocomplete = (props: Props): AutocompleteProps<Item> => {
  return useMemo(() => makeCountryAutocompleteProps(props), [props]);
};
